import { ElementIcon, useElementIcon } from "@/components/ui/icons";
import { TreeHeightWrapper } from "@/components/ui/tree/tree-height-wrapper";
import {
  TREE_NODE_HEIGHT,
  TreeNode,
  TreeNodeProps,
} from "@/components/ui/tree/tree-node";
import { useScansExportStore } from "@/modes/export-mode/scans-export/scans-export-store";
import { useIsExportableDataset } from "@/modes/export-mode/scans-export/use-prepare-scans-export-data";
import { useCurrentArea } from "@/modes/mode-data-context";
import { selectIsElementViewable } from "@/modes/mode-selectors";
import { setClippingBox } from "@/store/clipping-box-slice";
import { setIpxPointCloud } from "@/store/modes/ipx-order-mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { elementToTreeData, TreeData } from "@/utils/tree-generation/tree-data";
import {
  blue,
  FaroCheckbox,
  FaroText,
  grey,
  NoTranslate,
  SearchBar,
  TruncatedFaroText,
  useBreakpointMdUp,
} from "@faro-lotv/flat-ui";
import { convertToDateString, GUID } from "@faro-lotv/foundation";
import {
  IElement,
  IElementGenericPointCloudStream,
  isIElementGenericPointCloudStream,
} from "@faro-lotv/ielement-types";
import { selectChildDepthFirst } from "@faro-lotv/project-source";
import { Box, Stack, useMediaQuery } from "@mui/material";
import { isEqual } from "es-toolkit";
import { useCallback, useMemo, useState, type JSX } from "react";
import { NodeApi, RowRendererProps, Tree } from "react-arborist";
import { useDragDropManager } from "react-dnd";
import { collectScanIds, toggleIpxScanCheckbox } from "../ipx-scan-selection";
import { IpxScansDataProvider, useIpxScansData } from "../ipx-scans-data";

const TREE_MIN_HEIGHT = 240;
// Four touch rows, so the step sheet leaves room for the 3D view
const TREE_HEIGHT_SMALL = 176;
const TOUCH_ROW_HEIGHT = 44;

/** @returns the viewable point clouds of the current area, as the roots of the tree */
function usePointCloudNodes(): TreeData[] {
  const { dataSessions3d, dataSessions5d } = useCurrentArea();
  const pointCloudId = useAppSelector(
    (state) => state.ipxOrderMode.pointCloudId,
  );
  const isScanSelectionAvailable = useIsExportableDataset();
  const { clusterTree } = useIpxScansData();

  const pointClouds = useAppSelector(
    (state) =>
      [...dataSessions3d.pointClouds, ...dataSessions5d].flatMap((session) => {
        const pointCloud = selectChildDepthFirst(
          session,
          (el: IElement): el is IElementGenericPointCloudStream =>
            isIElementGenericPointCloudStream(el) &&
            selectIsElementViewable(el)(state),
        )(state);
        return pointCloud ? [{ session, pointCloud }] : [];
      }),
    isEqual,
  );

  return useMemo(
    () =>
      pointClouds.map(({ session, pointCloud }) => ({
        ...elementToTreeData({
          element: session,
          id: pointCloud.id,
          label: `${session.name} · ${convertToDateString(session.createdAt)}`,
        }),
        // Only one point cloud is shown at a time, so only its scans are listed
        children:
          pointCloud.id === pointCloudId && isScanSelectionAvailable
            ? clusterTree
            : null,
      })),
    [clusterTree, isScanSelectionAvailable, pointCloudId, pointClouds],
  );
}

/** @returns the point clouds of the area, with the scans of the selected one, to choose what is sent to IPX */
export function ScansPanel(): JSX.Element {
  return (
    <IpxScansDataProvider>
      <ScansPanelContent />
    </IpxScansDataProvider>
  );
}

function ScansPanelContent(): JSX.Element {
  const nodes = usePointCloudNodes();
  const [searchTerm, setSearchTerm] = useState("");
  const isMdUp = useBreakpointMdUp();
  const isTouch = useMediaQuery("(pointer: coarse)");
  // Touch-friendly rows on touch screens
  const rowHeight = isTouch ? TOUCH_ROW_HEIGHT : TREE_NODE_HEIGHT;

  if (!nodes.length) {
    return (
      <FaroText variant="bodyM" color={grey[700]}>
        There are no point clouds in this area.
      </FaroText>
    );
  }

  return (
    <Stack sx={{ gap: 1, flexGrow: 1 }}>
      <SearchBar
        variant="underlined"
        fullWidth
        placeholder="Search point clouds and scans"
        text={searchTerm}
        onTextChanged={setSearchTerm}
        onSearchReset={() => setSearchTerm("")}
      />
      <SelectionNote />
      <Box
        component="div"
        sx={{
          border: `1px solid ${grey[200]}`,
          borderRadius: 1,
          // Takes the room left in the step panel on large screens
          ...(isMdUp && {
            display: "flex",
            flexDirection: "column",
            flex: "1 1 0",
            minHeight: TREE_MIN_HEIGHT,
            overflow: "hidden",
          }),
        }}
      >
        {isMdUp ? (
          <TreeHeightWrapper>
            <PointCloudTree
              nodes={nodes}
              searchTerm={searchTerm}
              rowHeight={rowHeight}
            />
          </TreeHeightWrapper>
        ) : (
          // Shorter on small screens, so the 3D view stays usable above the step sheet
          <PointCloudTree
            nodes={nodes}
            searchTerm={searchTerm}
            height={TREE_HEIGHT_SMALL}
            rowHeight={rowHeight}
          />
        )}
      </Box>
      <FaroText variant="bodyS" color={grey[600]}>
        Check the scans and groups to send.
      </FaroText>
    </Stack>
  );
}

/** @returns why the point cloud can't be split in scans, nothing if it can */
function SelectionNote(): JSX.Element | null {
  if (useIsExportableDataset()) return null;
  return (
    <FaroText variant="bodyS" color={grey[700]} role="status">
      This point cloud can only be sent as a whole.
    </FaroText>
  );
}

type PointCloudTreeProps = {
  /** The point clouds, with the scans of the selected one */
  nodes: TreeData[];

  /** Filter of the visible nodes */
  searchTerm: string;

  /** Height of the tree in pixels, set by the TreeHeightWrapper when it fills its container */
  height?: number;

  /** Height of a row in pixels */
  rowHeight: number;
};

/** @returns the tree of the point clouds, with a checkbox to choose each scan or group */
function PointCloudTree({
  nodes,
  searchTerm,
  height,
  rowHeight,
}: PointCloudTreeProps): JSX.Element {
  const dragDropManager = useDragDropManager();
  const pointCloudId = useAppSelector(
    (state) => state.ipxOrderMode.pointCloudId,
  );

  // The selected point cloud starts expanded, its clusters collapsed to keep large datasets readable
  const initialOpenState = useMemo(
    () => (pointCloudId ? { [pointCloudId]: true } : undefined),
    [pointCloudId],
  );

  return (
    <Tree<TreeData>
      dndManager={dragDropManager}
      data={nodes}
      disableDrag
      disableDrop
      width="100%"
      height={height}
      rowHeight={rowHeight}
      indent={20}
      openByDefault={false}
      initialOpenState={initialOpenState}
      searchTerm={searchTerm}
      searchMatch={(node, term) =>
        node.data.label.toLowerCase().includes(term.toLowerCase())
      }
      renderRow={PointCloudTreeRow}
    >
      {PointCloudTreeNode}
    </Tree>
  );
}

/**
 * @param node The tree node
 * @returns the scans below the node, whether it's a point cloud, and if it's checked or partly checked
 */
function useNodeSelection(node: NodeApi<TreeData>): {
  scanIds: GUID[];
  isPointCloud: boolean;
  isChecked: boolean;
  isPartlyChecked: boolean;
} {
  const { invalidIds } = useIpxScansData();
  const selectedIds = useScansExportStore((state) => state.selectedIds);
  const pointCloudId = useAppSelector(
    (state) => state.ipxOrderMode.pointCloudId,
  );
  return useMemo(() => {
    const scanIds = collectScanIds([node.data], invalidIds);
    const selected = new Set(selectedIds);
    const isPartlySelected =
      scanIds.some((id) => selected.has(id)) &&
      !scanIds.every((id) => selected.has(id));
    // Everything in the shown point cloud is checked while it is sent as a whole
    const isInShownPointCloud = findRoot(node).id === pointCloudId;
    const isWhole = isInShownPointCloud && !selectedIds.length;
    return {
      scanIds,
      isPointCloud: node.level === 0,
      isChecked:
        isWhole ||
        (scanIds.length > 0 && scanIds.every((id) => selected.has(id))),
      isPartlyChecked: !isWhole && isPartlySelected,
    };
  }, [invalidIds, node, pointCloudId, selectedIds]);
}

function findRoot(node: NodeApi<TreeData>): NodeApi<TreeData> {
  return node.parent && node.parent.level >= 0 ? findRoot(node.parent) : node;
}

/** @returns the row of the tree: a click checks or unchecks it, on another point cloud it shows that one */
function PointCloudTreeRow({
  node,
  innerRef,
  attrs,
  children,
}: RowRendererProps<TreeData>): JSX.Element {
  const dispatch = useAppDispatch();
  const pointCloudId = useAppSelector(
    (state) => state.ipxOrderMode.pointCloudId,
  );
  const { invalidIds, clusterTree } = useIpxScansData();
  const { scanIds, isPointCloud } = useNodeSelection(node);
  const { multiSelectIds, multiHoverIds, clearHovering } =
    useScansExportStore();

  const toggle = useCallback(() => {
    // Another point cloud is shown and sent as a whole
    if (isPointCloud && node.id !== pointCloudId) {
      useScansExportStore.getState().clearSelection();
      dispatch(setClippingBox(undefined));
      dispatch(setActiveElement(node.id));
      dispatch(setIpxPointCloud(node.id));
      node.open();
      return;
    }
    const allScanIds = collectScanIds(clusterTree, invalidIds);
    const clicked = isPointCloud ? allScanIds : scanIds;
    if (!clicked.length) return;
    const { selectedIds } = useScansExportStore.getState();
    multiSelectIds(toggleIpxScanCheckbox(selectedIds, allScanIds, clicked));
  }, [
    clusterTree,
    dispatch,
    invalidIds,
    isPointCloud,
    multiSelectIds,
    node,
    pointCloudId,
    scanIds,
  ]);

  return (
    <Box
      component="div"
      ref={innerRef}
      {...attrs}
      onClick={toggle}
      onPointerEnter={() => multiHoverIds(scanIds)}
      onPointerLeave={clearHovering}
    >
      {children}
    </Box>
  );
}

function PointCloudTreeNode({
  node,
  style,
}: TreeNodeProps<TreeData>): JSX.Element {
  const icon = useElementIcon(node.data.element, node.data.directParent);
  const { scanIds, isPointCloud, isChecked, isPartlyChecked } =
    useNodeSelection(node);

  return (
    <Box component="div" style={{ ...style, height: "100%" }}>
      <TreeNode<TreeData>
        node={node}
        collapseIcon
        // Clusters without scans that can be ordered
        isDisabled={!isPointCloud && !scanIds.length}
        // Hovered items turn blue
        hoverBackgroundColor={blue[100]}
        shouldExpandNodeOnClick={false}
      >
        <FaroCheckbox
          checked={isChecked}
          indeterminate={isPartlyChecked}
          disabled={!isPointCloud && !scanIds.length}
          // The click is handled by the row, as for the rest of the row
          onChange={noop}
          controlSx={{ m: 0, mr: 0.5 }}
        />
        {/* Inherit the color of the row, e.g. grey when disabled */}
        <ElementIcon
          icon={icon}
          sx={{ fontSize: "1.125em", mr: 0.75, color: "inherit" }}
        />
        <TruncatedFaroText
          variant="bodyM"
          sx={{ color: "inherit" }}
          tooltip={<NoTranslate>{node.data.label}</NoTranslate>}
        >
          <NoTranslate>{node.data.label}</NoTranslate>
        </TruncatedFaroText>
      </TreeNode>
    </Box>
  );
}

function noop(): void {}
